import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import InputIcon from '@mui/icons-material/Input';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import axios from 'axios';
import { supabase } from './lib/supabase';
import {
  getBalanceSheetItems,
  addBalanceSheetItem,
  updateBalanceSheetItem,
  deleteBalanceSheetItem,
  getCashFlowItems,
  addCashFlowItem,
  updateCashFlowItem,
  deleteCashFlowItem,
  getUserProfile
} from './lib/database';

import BalanceSheetForm from './components/BalanceSheetForm';
import CashFlowForm from './components/CashFlowForm';
import SummaryCards from './components/SummaryCards';
import DashboardCharts from './components/DashboardCharts';
import Login from './components/Login';
import Register from './components/Register';
import InputPage from './components/InputPage';
import Onboarding from './components/Onboarding';

// Create theme instances
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
    },
    secondary: {
      main: '#f50057',
      light: '#ff4081',
      dark: '#c51162',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c3e50',
      secondary: '#546e7a',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 700,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
      light: '#e3f2fd',
      dark: '#42a5f5',
    },
    secondary: {
      main: '#f48fb1',
      light: '#fce4ec',
      dark: '#f06292',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0bec5',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 700,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
  },
});

function Dashboard({ user }) {
  const [balanceSheetItems, setBalanceSheetItems] = useState([]);
  const [cashFlowItems, setCashFlowItems] = useState([]);
  const [balanceSheetOpen, setBalanceSheetOpen] = useState(false);
  const [cashFlowOpen, setCashFlowOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [darkMode, setDarkMode] = useState(prefersDarkMode);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [balanceSheet, cashFlow] = await Promise.all([
        getBalanceSheetItems(),
        getCashFlowItems()
      ]);
      setBalanceSheetItems(balanceSheet);
      setCashFlowItems(cashFlow);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBalanceSheetSubmit = async (data) => {
    try {
      console.log('Submitting balance sheet data:', data);
      if (editingItem) {
        console.log('Updating existing item:', editingItem.id);
        const updated = await updateBalanceSheetItem(editingItem.id, data);
        setBalanceSheetItems(items =>
          items.map(item => item.id === editingItem.id ? updated : item)
        );
      } else {
        console.log('Adding new item');
        const added = await addBalanceSheetItem(data);
        setBalanceSheetItems(items => [added, ...items]);
      }
      setBalanceSheetOpen(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Error saving balance sheet item:', err);
      setError(err.message);
    }
  };

  const handleCashFlowSubmit = async (data) => {
    try {
      console.log('Submitting cash flow data:', data);
      if (editingItem) {
        console.log('Updating existing item:', editingItem.id);
        const updated = await updateCashFlowItem(editingItem.id, data);
        setCashFlowItems(items =>
          items.map(item => item.id === editingItem.id ? updated : item)
        );
      } else {
        console.log('Adding new item');
        const added = await addCashFlowItem(data);
        setCashFlowItems(items => [added, ...items]);
      }
      setCashFlowOpen(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Error saving cash flow item:', err);
      setError(err.message);
    }
  };

  const handleDelete = async (type, id) => {
    try {
      if (type === 'balance-sheet') {
        await deleteBalanceSheetItem(id);
        setBalanceSheetItems(items => items.filter(item => item.id !== id));
      } else {
        await deleteCashFlowItem(id);
        setCashFlowItems(items => items.filter(item => item.id !== id));
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      // You might want to show this error to the user
    }
  };

  const handleEdit = (type, item) => {
    try {
      console.log('Handling edit:', { type, item });
      setEditingItem(item);
      if (type === 'balance-sheet') {
        setBalanceSheetOpen(true);
      } else {
        setCashFlowOpen(true);
      }
    } catch (error) {
      console.error('Error in handleEdit:', error);
      setError(error.message);
    }
  };

  const calculateTotal = (items, type) => {
    return items
      .filter(item => item.type === type)
      .reduce((sum, item) => sum + item.amount, 0);
  };

  const calculateAnnualizedAmount = (item) => {
    const annualMultipliers = {
      'one-time': 0,
      'daily': 365,
      'weekly': 52,
      'fortnightly': 26,
      'monthly': 12,
      'quarterly': 4,
      'annually': 1
    };
    return item.amount * annualMultipliers[item.frequency];
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="static" elevation={0} sx={{ backgroundColor: 'background.paper', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
          <Toolbar>
            <Box
              component="img"
              src="/vercel.ico"
              alt="Path2Wealth"
              sx={{
                width: 32,
                height: 32,
                mr: 2,
                color: 'primary.main'
              }}
            />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'text.primary', fontWeight: 600 }}>
              Path2Wealth
            </Typography>
            <IconButton onClick={toggleDarkMode} color="primary" title={darkMode ? "Switch to light mode" : "Switch to dark mode"}>
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            <IconButton color="inherit" component={Link} to="/input">
              <InputIcon />
            </IconButton>
            <IconButton onClick={handleLogout} color="primary" title="Logout">
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={handleTabChange} aria-label="dashboard tabs">
              <Tab label="Overview" />
              <Tab label="Balance Sheet" />
              <Tab label="Cash Flow" />
            </Tabs>
          </Box>
          
          {currentTab === 0 && (
            <>
              <SummaryCards 
                balanceSheetItems={balanceSheetItems} 
                cashFlowItems={cashFlowItems} 
              />
              
              <Box sx={{ mt: 4, mb: 4 }}>
                <DashboardCharts
                  balanceSheetItems={balanceSheetItems}
                  cashFlowItems={cashFlowItems}
                />
              </Box>
            </>
          )}
          
          {currentTab === 1 && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6">Balance Sheet</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingItem(null);
                    setBalanceSheetOpen(true);
                  }}
                >
                  Add Item
                </Button>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {balanceSheetItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            {item.date ? new Date(item.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : new Date().toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </TableCell>
                          <TableCell>{item.category}</TableCell>
                          <TableCell>${item.amount.toLocaleString()}</TableCell>
                          <TableCell>
                            <IconButton onClick={() => handleEdit('balance-sheet', item)}>
                              <EditIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDelete('balance-sheet', item.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}
          
          {currentTab === 2 && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6">Cash Flow</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingItem(null);
                    setCashFlowOpen(true);
                  }}
                >
                  Add Item
                </Button>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Frequency</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cashFlowItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.category}</TableCell>
                          <TableCell>${item.amount.toLocaleString()}</TableCell>
                          <TableCell>{item.frequency}</TableCell>
                          <TableCell>
                            <IconButton onClick={() => handleEdit('cashFlow', item)}>
                              <EditIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDelete('cashFlow', item.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}
        </Container>

        <BalanceSheetForm
          open={balanceSheetOpen}
          onClose={() => {
            setBalanceSheetOpen(false);
            setEditingItem(null);
          }}
          onSubmit={handleBalanceSheetSubmit}
          editingItem={editingItem}
        />

        <CashFlowForm
          open={cashFlowOpen}
          onClose={() => {
            setCashFlowOpen(false);
            setEditingItem(null);
          }}
          onSubmit={handleCashFlowSubmit}
          editingItem={editingItem}
        />
      </Box>
    </ThemeProvider>
  );
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [darkMode, setDarkMode] = useState(prefersDarkMode);
  const navigate = useNavigate();

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkOnboardingStatus(session.user);
      } else {
        setLoading(false);
      }
    });

    // Listen for changes on auth state (logged in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        await checkOnboardingStatus(session.user);
      } else {
        setOnboardingCompleted(false);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkOnboardingStatus = async (user) => {
    try {
      const profile = await getUserProfile();
      setOnboardingCompleted(profile?.onboarding_completed ?? false);
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      setOnboardingCompleted(false);
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingComplete = () => {
    setOnboardingCompleted(true);
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <Routes>
        <Route
          path="/"
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/login"
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Login onLogin={setUser} />
            )
          }
        />
        <Route
          path="/register"
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Register onRegister={setUser} />
            )
          }
        />
        <Route
          path="/onboarding"
          element={
            user ? (
              onboardingCompleted ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Onboarding onComplete={handleOnboardingComplete} />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            user ? (
              onboardingCompleted ? (
                <Dashboard user={user} />
              ) : (
                <Navigate to="/onboarding" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </ThemeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
